import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Loader2, Plus } from 'lucide-react';
import { StarterRecipe } from '@/services/starterRecipe';
import RecipeImage from '@/components/recipe/RecipeImage';

interface StarterRecipeCardProps {
  recipe: StarterRecipe;
  onAdd: (recipe: StarterRecipe) => void;
  isAdded: boolean;
  isAdding: boolean;
}

const StarterRecipeCard: React.FC<StarterRecipeCardProps> = ({
  recipe,
  onAdd,
  isAdded,
  isAdding
}) => {
  // Convert StarterRecipe to Recipe-like object for RecipeImage
  const recipeForImage = {
    id: recipe.id,
    title: recipe.title,
    image_url: recipe.image_url,
    storage_image_url: recipe.storage_image_url,
    storage_image_path: recipe.storage_image_path,
    ingredients: recipe.ingredients,
    tags: recipe.tags,
  };

  return (
    <Card className={`overflow-hidden transition-all duration-200 hover:shadow-md h-full flex flex-col ${
      isAdded ? 'ring-2 ring-sage/50 bg-sage/5' : ''
    }`}>
      <div className="aspect-[4/3] relative overflow-hidden bg-muted flex-shrink-0">
        <RecipeImage 
          recipe={recipeForImage}
          className="w-full h-full object-cover"
          size="md"
        />
      </div>
      <CardContent className="p-3 flex flex-col flex-1">
        <h4 className="font-medium text-sm line-clamp-2 leading-tight min-h-[2.5rem]">
          {recipe.title}
        </h4>
        
        <div className="flex items-center gap-1.5 flex-wrap mt-2 mb-3 min-h-[3.5rem]">
          {recipe.tags?.slice(0, 3).map((tag) => (
            <Badge key={tag} variant="secondary" className="text-xs px-1.5 py-0">
              {tag}
            </Badge>
          ))}
        </div>

        <Button
          size="sm"
          className={`w-full mt-auto ${
            isAdded 
              ? 'bg-sage/20 text-sage hover:bg-sage/30 border border-sage/30' 
              : 'bg-sage hover:bg-sage/90'
          }`}
          onClick={() => onAdd(recipe)}
          disabled={isAdded || isAdding}
        >
          {isAdding ? (
            <>
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              Adding...
            </>
          ) : isAdded ? (
            <>
              <Check className="h-3.5 w-3.5 mr-1.5" />
              Added!
            </>
          ) : (
            <>
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default StarterRecipeCard;
